import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, LockKeyhole, Mail, ShieldCheck } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import logoAzul from '../../assets/branding/lbcc-azul.png';
import logoBranco from '../../assets/branding/lbcc-branco.png';
import loginHero from '../../assets/branding/login-hero-lbcc.jpg';
import loginTeam1 from '../../assets/branding/login-team-1.jpg';
import loginTeam2 from '../../assets/branding/login-team-2.jpg';
import loginTeam3 from '../../assets/branding/login-team-3.jpg';
import loginTeam4 from '../../assets/branding/login-team-4.jpg';

const panelVariants = {
  initial: { opacity: 0, y: 24 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: 0.12, ease: [0.22, 1, 0.36, 1] },
  },
};

const slideVariants = {
  initial: { opacity: 0, scale: 1.04 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] },
  },
  exit: {
    opacity: 0,
    scale: 1.02,
    transition: { duration: 0.55, ease: [0.4, 0, 0.2, 1] },
  },
};

const overlayCardVariants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: 0.18, ease: [0.22, 1, 0.36, 1] },
  },
  exit: {
    opacity: 0,
    y: 12,
    transition: { duration: 0.35, ease: [0.4, 0, 0.2, 1] },
  },
};

const showcaseSlides = [
  {
    id: 'especialista-lbcc',
    badge: 'Especialista LBCC',
    image: loginHero,
    position: 'object-[44%_24%]',
    headline: 'Controle completo da operação em um só lugar.',
    description: 'Acompanhe clientes, tarefas, documentos e aprovações com visão centralizada e leitura rápida.',
    chips: ['Controle diário', 'Fluxo seguro'],
    metrics: [
      { label: 'Clientes', value: 'Visão consolidada' },
      { label: 'Aprovações', value: 'Mais agilidade' },
    ],
  },
  {
    id: 'equipe-lbcc',
    badge: 'Equipe profissional',
    image: loginTeam1,
    position: 'object-center',
    headline: 'Mais integração entre equipe, cliente e rotina contábil.',
    description: 'Visualize cada etapa com mais clareza, reduzindo retrabalho e melhorando a comunicação operacional.',
    chips: ['Equipe alinhada', 'Ação rápida'],
    metrics: [
      { label: 'Comunicação', value: 'Fluxo contínuo' },
      { label: 'Produtividade', value: 'Menos retrabalho' },
    ],
  },
  {
    id: 'gestao-lbcc',
    badge: 'Atendimento consultivo',
    image: loginTeam2,
    position: 'object-center',
    headline: 'Decisão com contexto, histórico e produtividade.',
    description: 'O sistema organiza pendências, documentos e interações para dar mais controle ao escritório.',
    chips: ['Documentos centralizados', 'Produtividade real'],
    metrics: [
      { label: 'Histórico', value: 'Tudo rastreável' },
      { label: 'Operação', value: 'Mais previsível' },
    ],
  },
  {
    id: 'rotina-controlada',
    badge: 'Gestão da rotina',
    image: loginTeam3,
    position: 'object-center',
    headline: 'Sua equipe trabalha com mais clareza, ritmo e acompanhamento.',
    description: 'O hub mostra prioridades, pendências e próximos passos sem depender de planilhas soltas ou mensagens perdidas.',
    chips: ['Prioridades visíveis', 'Rotina organizada'],
    metrics: [
      { label: 'Pendências', value: 'Leitura instantânea' },
      { label: 'Execução', value: 'Acompanhamento real' },
    ],
  },
  {
    id: 'controle-lbcc',
    badge: 'Controle operacional',
    image: loginTeam4,
    position: 'object-center',
    headline: 'Mais controle para o escritório e mais segurança para o cliente.',
    description: 'Unifique tarefas, documentos, comunicação e checkpoints em uma experiência mais elegante e confiável.',
    chips: ['Checkpoints claros', 'Experiência premium'],
    metrics: [
      { label: 'Cliente', value: 'Resposta mais rápida' },
      { label: 'Equipe', value: 'Mais coordenação' },
    ],
  },
];

export default function PremiumLoginPage({
  formData,
  setFormData,
  handleSubmit,
  loading,
  error,
}) {
  const [activeSlide, setActiveSlide] = React.useState(0);

  React.useEffect(() => {
    const interval = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % showcaseSlides.length);
    }, 4600);

    return () => window.clearInterval(interval);
  }, []);

  const currentSlide = showcaseSlides[activeSlide];
  const nextSlides = [
    showcaseSlides[(activeSlide + 1) % showcaseSlides.length],
    showcaseSlides[(activeSlide + 2) % showcaseSlides.length],
  ];

  return (
    <div className="min-h-screen bg-white text-zinc-950 font-plex" data-testid="login-page">
      <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
        <div className="flex min-h-screen flex-col bg-white px-6 py-8 sm:px-10 lg:px-16 lg:py-10">
          <div className="flex items-center gap-4" data-testid="login-brand-bar">
            <div className="flex h-14 w-14 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm" data-testid="login-brand-logo-orb">
              <img src={logoAzul} alt="LBCC" className="h-11 w-11 object-contain" data-testid="login-brand-logo" />
            </div>
            <div data-testid="login-brand-copy">
              <div className="font-manrope text-xl font-semibold tracking-tight text-zinc-950">LBCCorex HUB</div>
              <div className="text-sm text-zinc-500">Sistema operacional LBCC</div>
            </div>
          </div>

          <motion.div
            className="my-auto w-full max-w-[28rem]"
            initial="initial"
            animate="animate"
            variants={panelVariants}
            data-testid="login-form-card"
          >
            <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-[#0D2B4A]" data-testid="login-security-badge">
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
              <span>Ambiente protegido</span>
            </div>

            <div className="space-y-3" data-testid="login-heading-block">
              <div className="h-1 w-20 rounded-full bg-emerald-500" data-testid="login-heading-accent" />
              <h1 className="font-manrope text-4xl font-semibold text-[#0D2B4A] sm:text-5xl" data-testid="login-heading">
                Acesse o hub LBCC
              </h1>
            </div>

            {error ? (
              <div
                className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
                data-testid="login-error-alert"
              >
                {error}
              </div>
            ) : null}

            <form className="mt-8 space-y-5" onSubmit={handleSubmit} data-testid="login-form">
              <div className="space-y-2" data-testid="login-email-field">
                <label className="text-sm font-medium text-zinc-700" htmlFor="login-email-input-visible" data-testid="login-email-label">
                  Email
                </label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                  <Input
                    id="login-email-input-visible"
                    type="email"
                    value={formData.email}
                    onChange={(event) => setFormData({ ...formData, email: event.target.value })}
                    placeholder="seuemail@empresa.com.br"
                    autoComplete="email"
                    aria-label="Email"
                    required
                    className="h-14 rounded-xl border-zinc-200 bg-white pl-11 text-base text-zinc-950 placeholder:text-zinc-400 focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2" data-testid="login-password-field">
                <div className="flex items-center justify-between gap-3">
                  <label className="text-sm font-medium text-zinc-700" htmlFor="login-password-input-visible" data-testid="login-password-label">
                    Senha
                  </label>
                  <a
                    href="mailto:lbcc@lucasberto.com.br?subject=Recupera%C3%A7%C3%A3o%20de%20acesso%20LBCC"
                    className="text-sm font-medium text-emerald-700 transition-colors hover:text-emerald-800"
                    data-testid="login-forgot-password-link"
                  >
                    Recuperar acesso
                  </a>
                </div>
                <div className="relative">
                  <LockKeyhole className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                  <Input
                    id="login-password-input-visible"
                    type="password"
                    value={formData.password}
                    onChange={(event) => setFormData({ ...formData, password: event.target.value })}
                    placeholder="Digite sua senha"
                    autoComplete="current-password"
                    aria-label="Senha"
                    required
                    className="h-14 rounded-xl border-zinc-200 bg-white pl-11 text-base text-zinc-950 placeholder:text-zinc-400 focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="group relative h-14 w-full overflow-hidden rounded-xl bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-600 text-white shadow-[0_18px_40px_rgba(5,150,105,0.28)] transition-all duration-300 hover:shadow-[0_22px_50px_rgba(5,150,105,0.42)] hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-70"
                data-testid="login-submit-button"
              >
                <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                <span className="relative text-sm font-semibold tracking-[0.2em] uppercase">{loading ? 'Entrando' : 'Entrar'}</span>
                <ArrowRight className="relative transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </form>
          </motion.div>
        </div>

        <div className="relative hidden min-h-screen overflow-hidden bg-[#071B30] text-white lg:flex" data-testid="login-showcase-panel">
          <AnimatePresence mode="wait">
            <motion.img
              key={currentSlide.id}
              src={currentSlide.image}
              alt={currentSlide.badge}
              className={`absolute inset-0 h-full w-full object-cover ${currentSlide.position}`}
              variants={slideVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              data-testid="login-showcase-background"
            />
          </AnimatePresence>

          <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(7,27,48,0.88)_0%,rgba(13,43,74,0.58)_45%,rgba(7,27,48,0.94)_100%)]" />
          <div className="absolute inset-y-0 left-0 w-48 bg-[linear-gradient(90deg,rgba(7,27,48,0.82)_0%,rgba(7,27,48,0)_100%)]" />
          <div className="absolute inset-x-0 bottom-0 h-48 bg-[linear-gradient(180deg,rgba(7,27,48,0)_0%,rgba(7,27,48,0.66)_60%,rgba(7,27,48,0.96)_100%)]" />

          <motion.div
            className="absolute right-[-4rem] top-[10%] h-72 w-72 rounded-full bg-emerald-500/14 blur-3xl"
            animate={{ y: [0, -18, 0], x: [0, 10, 0], opacity: [0.28, 0.48, 0.28] }}
            transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
            aria-hidden="true"
          />
          <motion.div
            className="absolute bottom-[12%] right-[10%] h-48 w-48 rounded-full bg-emerald-400/10 blur-3xl"
            animate={{ y: [0, 14, 0], scale: [1, 1.08, 1] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}
            aria-hidden="true"
          />
          <motion.div
            className="absolute inset-y-0 right-[18%] w-px bg-gradient-to-b from-transparent via-white/10 to-transparent"
            animate={{ opacity: [0.18, 0.45, 0.18], scaleY: [0.94, 1, 0.94] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
            aria-hidden="true"
          />

          <div className="relative z-10 flex h-full w-full flex-col justify-between p-12 xl:p-16">
            <div className="flex items-center gap-4" data-testid="login-showcase-brand">
              <div className="flex h-14 w-14 items-center justify-center rounded-full border border-white/20 bg-white/10 backdrop-blur-sm" data-testid="login-showcase-brand-logo-orb">
                <img src={logoBranco} alt="LBCC" className="h-11 w-11 object-contain" data-testid="login-showcase-brand-logo" />
              </div>
              <div>
                <div className="font-manrope text-lg font-semibold tracking-tight text-white" data-testid="login-showcase-brand-name">LBCCorex HUB</div>
                <div className="text-sm text-white/78" data-testid="login-showcase-brand-subtitle">Sistema operacional LBCC</div>
              </div>
            </div>

            <div className="flex flex-1 items-center justify-end">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide.id}
                  className="hidden w-[26rem] rounded-[2rem] border border-white/10 bg-[#071B30]/60 p-6 shadow-[0_30px_90px_rgba(7,27,48,0.38)] backdrop-blur-md xl:block"
                  variants={overlayCardVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  data-testid="login-showcase-accent-card"
                >
                  <div className="mb-4 flex items-center gap-3">
                    <div className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                    <div className="text-[11px] uppercase tracking-[0.28em] text-white/65" data-testid="login-showcase-card-badge">
                      {currentSlide.badge}
                    </div>
                  </div>

                  <div className="space-y-3" data-testid="login-showcase-card-copy">
                    <h2 className="font-manrope text-3xl font-semibold leading-tight text-white" data-testid="login-showcase-card-title">
                      {currentSlide.headline}
                    </h2>
                    <p className="text-sm leading-7 text-white/72" data-testid="login-showcase-card-description">
                      {currentSlide.description}
                    </p>
                  </div>

                  <div className="mt-6 h-px w-full bg-gradient-to-r from-white/0 via-white/14 to-white/0" />

                  <div className="mt-6 grid grid-cols-2 gap-3" data-testid="login-showcase-card-chips">
                    {currentSlide.chips.map((chip) => (
                      <div
                        key={chip}
                        className="rounded-2xl border border-white/10 bg-[#0D2B4A]/55 px-4 py-4 text-sm text-white/88"
                        data-testid={`login-showcase-chip-${chip.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      >
                        {chip}
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3" data-testid="login-showcase-card-metrics">
                    {currentSlide.metrics.map((metric) => (
                      <div
                        key={metric.label}
                        className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3"
                        data-testid={`login-showcase-metric-${metric.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      >
                        <div className="text-[11px] uppercase tracking-[0.22em] text-white/45">{metric.label}</div>
                        <div className="mt-2 text-sm text-white/90">{metric.value}</div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="space-y-6" data-testid="login-showcase-content">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`${currentSlide.id}-caption`}
                  className="max-w-lg"
                  variants={overlayCardVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  data-testid="login-showcase-caption"
                >
                  <div className="mb-3 h-1 w-16 rounded-full bg-emerald-400" data-testid="login-showcase-gold-line" />
                  <div className="text-[11px] uppercase tracking-[0.28em] text-white/60" data-testid="login-showcase-person-name">
                    {currentSlide.badge}
                  </div>
                  <div className="mt-3 font-manrope text-3xl font-semibold tracking-tight text-white" data-testid="login-showcase-caption-title">
                    {currentSlide.headline}
                  </div>
                  <p className="mt-4 max-w-xl text-base leading-7 text-white/72" data-testid="login-showcase-caption-description">
                    {currentSlide.description}
                  </p>
                </motion.div>
              </AnimatePresence>

              <div className="flex items-end justify-between gap-6" data-testid="login-showcase-lower-row">
                <div>
                  <div className="flex items-center gap-3" data-testid="login-showcase-indicators">
                    {showcaseSlides.map((slide, index) => {
                      const isActive = index === activeSlide;
                      return (
                        <button
                          key={slide.id}
                          type="button"
                          onClick={() => setActiveSlide(index)}
                          className={`h-2.5 rounded-full transition-all duration-300 ${isActive ? 'w-12 bg-emerald-400' : 'w-2.5 bg-white/35 hover:bg-white/55'}`}
                          aria-label={`Mostrar slide ${index + 1}`}
                          data-testid={`login-showcase-indicator-${index}`}
                        />
                      );
                    })}
                  </div>

                  <motion.div
                    className="mt-4 text-[11px] uppercase tracking-[0.26em] text-white/48"
                    animate={{ opacity: [0.45, 0.85, 0.45] }}
                    transition={{ duration: 3.6, repeat: Infinity, ease: 'easeInOut' }}
                    data-testid="login-showcase-loop-label"
                  >
                    gestão · equipe · documentos · controle
                  </motion.div>
                </div>

                <div className="hidden items-center gap-3 xl:flex" data-testid="login-showcase-thumbnail-rail">
                  {nextSlides.map((slide, index) => (
                    <button
                      key={slide.id}
                      type="button"
                      onClick={() => setActiveSlide((activeSlide + index + 1) % showcaseSlides.length)}
                      className="group relative h-20 w-20 overflow-hidden rounded-2xl border border-white/10 bg-white/5"
                      data-testid={`login-showcase-thumbnail-${index}`}
                    >
                      <img src={slide.image} alt={slide.badge} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.12)_0%,rgba(2,6,23,0.58)_100%)]" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
